'use client'

import { useCallback, useEffect, useState } from 'react'

/**
 * Mosaïque éditoriale cliquable (Révélation / Notre Histoire / L'Art de briller).
 * Desktop : grille 4 colonnes (1re tuile portrait haute, tuiles larges paysage).
 * Mobile : masonry « quinconce » 2 colonnes (ratios portrait/paysage), + une
 * 6e image paysage réservée au mobile pour combler le vide.
 * Un clic sur n'importe quelle image ouvre une lightbox qui parcourt les images
 * de la mosaïque en atterrissant sur celle cliquée.
 */
const ACCENT = '#C2A66B'

export type MosaicImage = { src: string; o: 'portrait' | 'paysage'; pos?: string }

export default function Mosaic({ images, uid = 'mos' }: { images: MosaicImage[]; uid?: string }) {
  const [open, setOpen] = useState(false)
  const [idx, setIdx] = useState(0)
  const count = images.length

  const show = useCallback((i: number) => { setIdx(i); setOpen(true) }, [])
  const close = useCallback(() => setOpen(false), [])
  const prev = useCallback(() => setIdx((i) => (i - 1 + count) % count), [count])
  const next = useCallback(() => setIdx((i) => (i + 1) % count), [count])

  useEffect(() => {
    if (!open) return
    document.body.style.overflow = 'hidden'
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close()
      else if (e.key === 'ArrowLeft') prev()
      else if (e.key === 'ArrowRight') next()
    }
    document.addEventListener('keydown', onKey)
    return () => { document.body.style.overflow = ''; document.removeEventListener('keydown', onKey) }
  }, [open, close, prev, next])

  const cur = images[idx]

  return (
    <>
      <div className={`mos-gal ${uid}`}>
        {images.map((g, i) => (
          <button
            key={i}
            type="button"
            onClick={() => show(i)}
            aria-label={`Agrandir l'image ${i + 1}`}
            className={`mos-tile ${g.o}${i === 0 ? ' mos-big' : i === 1 || i === 4 ? ' mos-wide' : ''}${i === 5 ? ' mos-extra' : ''}`}
          >
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img src={g.src} alt="" loading="lazy" style={{ objectPosition: g.pos || '50% 50%' }} />
          </button>
        ))}
      </div>

      {open && cur && (
        <div role="dialog" aria-modal="true" onClick={close}
          style={{ position: 'fixed', inset: 0, zIndex: 10000, background: 'rgba(4,10,24,0.96)', display: 'flex', alignItems: 'center', justifyContent: 'center', backdropFilter: 'blur(6px)' }}>
          <button type="button" onClick={close} aria-label="Fermer" style={{ position: 'fixed', top: 20, right: 22, zIndex: 2, width: 44, height: 44, borderRadius: 999, border: `1px solid ${ACCENT}55`, background: 'rgba(0,0,0,0.25)', color: '#fff', fontSize: 22, lineHeight: 1, cursor: 'pointer' }}>×</button>
          <button type="button" onClick={(e) => { e.stopPropagation(); prev() }} aria-label="Précédent" className="mos-nav" style={{ left: 'clamp(10px, 2vw, 28px)' }}>‹</button>
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img onClick={(e) => e.stopPropagation()} src={cur.src} alt="" style={{ maxWidth: '92vw', maxHeight: '86vh', objectFit: 'contain', boxShadow: '0 30px 80px rgba(0,0,0,0.5)' }} />
          <button type="button" onClick={(e) => { e.stopPropagation(); next() }} aria-label="Suivant" className="mos-nav" style={{ right: 'clamp(10px, 2vw, 28px)' }}>›</button>
          <div style={{ position: 'fixed', bottom: 20, left: 0, right: 0, textAlign: 'center', color: 'rgba(255,255,255,0.55)', fontSize: 12, letterSpacing: '0.1em' }}>{idx + 1} / {count}</div>
        </div>
      )}

      <style>{`
        .mos-gal { display: grid; grid-template-columns: repeat(4, 1fr); grid-auto-rows: 190px; gap: 12px; margin-top: 44px; }
        .mos-tile { position: relative; overflow: hidden; padding: 0; border: 1px solid ${ACCENT}40; background: linear-gradient(150deg, #0a2247, #04173a); cursor: pointer; }
        .mos-tile img { position: absolute; inset: 0; width: 100%; height: 100%; object-fit: cover; transition: transform .7s ease; }
        .mos-tile:hover img { transform: scale(1.05); }
        .mos-big { grid-row: span 2; }
        .mos-wide { grid-column: span 2; }
        .mos-extra { display: none; }
        .mos-nav { position: fixed; top: 50%; transform: translateY(-50%); z-index: 2; width: 52px; height: 52px; border-radius: 999px; border: 1px solid rgba(194,166,107,0.4); background: rgba(0,0,0,0.28); color: #fff; font-size: 30px; line-height: 1; cursor: pointer; display: flex; align-items: center; justify-content: center; }
        .mos-nav:hover { background: rgba(194,166,107,0.25); }
        @media (max-width: 820px) {
          .mos-gal { display: block; column-count: 2; column-gap: 12px; grid-auto-rows: auto; }
          .mos-tile { break-inside: avoid; -webkit-column-break-inside: avoid; margin-bottom: 12px; width: 100%; display: block; grid-row: auto; grid-column: auto; }
          .mos-tile.portrait { aspect-ratio: 3 / 4; }
          .mos-tile.paysage { aspect-ratio: 4 / 3; }
          .mos-extra { display: block; }
        }
      `}</style>
    </>
  )
}
